/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Activity, AlertCircle, AlertTriangle, CheckCircle2, FlaskConical, Gauge, Globe,
  Layers, Loader2, RefreshCw, ShieldAlert, Sparkles, Trash2, Zap,
} from 'lucide-react';
import { holidayAPI } from '../../lib/api';

const HEALTH_STYLES = {
  healthy: { dot: 'bg-emerald-500', cls: 'border-emerald-200 bg-emerald-50 text-emerald-800', label: 'Healthy', icon: CheckCircle2 },
  warning: { dot: 'bg-amber-500', cls: 'border-amber-200 bg-amber-50 text-amber-800', label: 'Warning', icon: AlertTriangle },
  critical: { dot: 'bg-rose-500', cls: 'border-rose-200 bg-rose-50 text-rose-800', label: 'Critical', icon: AlertCircle },
};

// ─────────────────────────────────────────────────────────────────────────
// 1) Coverage Health + Resolver Metrics + Sync-failure red badge
//    Rendered inside the existing Diagnostics tab
// ─────────────────────────────────────────────────────────────────────────
export function CoverageAndMetricsPanel() {
  const [coverage, setCoverage] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([holidayAPI.coverageHealth(), holidayAPI.resolverMetrics()]);
      setCoverage(c.data);
      setMetrics(m.data);
    } catch {/* non-critical */}
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 20000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const handleClearCache = async () => {
    setClearing(true);
    try {
      await holidayAPI.clearCache();
      toast.success('Resolver cache cleared');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear cache');
    } finally {
      setClearing(false);
    }
  };

  const criticalCount = (coverage?.items || []).filter((c) => c.status === 'critical').length;
  const warningCount = (coverage?.items || []).filter((c) => c.status === 'warning').length;

  return (
    <div className="space-y-4">
      {/* Sync failure / health summary banner */}
      {(criticalCount > 0 || warningCount > 0) && (
        <div
          data-testid="holiday-sync-alert-badge"
          className={`flex items-start gap-3 p-3 rounded-lg border ${
            criticalCount > 0 ? 'border-rose-200 bg-rose-50 text-rose-900' : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
          title={
            criticalCount > 0
              ? `${criticalCount} country/countries with critical sync issues`
              : `${warningCount} country/countries with warnings`
          }
        >
          {criticalCount > 0
            ? <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
            : <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />}
          <div className="text-sm">
            <div className="font-medium">
              {criticalCount > 0
                ? `${criticalCount} country has critical sync issues`
                : `${warningCount} country needs attention`}
            </div>
            <div className="text-xs opacity-80 mt-0.5">
              See the Coverage Health cards below for details. App functionality continues normally.
            </div>
          </div>
        </div>
      )}

      {/* Coverage Health cards */}
      <div className="rounded-lg border p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-slate-500" />
            <h3 className="font-medium">Coverage Health</h3>
          </div>
          <span className="text-xs text-slate-400">
            {coverage ? `Generated ${new Date(coverage.generated_at).toLocaleTimeString()}` : '—'}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(coverage?.items || []).map((c) => {
            const s = HEALTH_STYLES[c.status] || HEALTH_STYLES.warning;
            const Icon = s.icon;
            return (
              <div
                key={c.country_code}
                className={`rounded-lg border p-3 ${s.cls}`}
                data-testid={`coverage-card-${c.country_code}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="font-semibold">{c.country_code}</span>
                  </div>
                  <span className="text-xs font-medium uppercase tracking-wide">{s.label}</span>
                </div>
                <div className="mt-2 text-xs space-y-0.5">
                  <div>Year {new Date().getFullYear()}: <b>{c.current_year_count}</b> rows</div>
                  <div>Next year: <b>{c.next_year_count}</b> rows</div>
                  <div>Provinces: <b>{(c.province_codes || []).length}</b></div>
                  <div>Score: <b>{c.score}</b>/100</div>
                </div>
                {(c.issues || []).length > 0 && (
                  <ul className="mt-2 text-xs list-disc pl-4 space-y-0.5">
                    {c.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Resolver Metrics card */}
      <div className="rounded-lg border p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-slate-500" />
            <h3 className="font-medium">Resolver Metrics</h3>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleClearCache}
            disabled={clearing}
            data-testid="clear-resolver-cache-btn"
          >
            {clearing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
            Clear Cache
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="resolver-metrics-grid">
          <Metric label="Cache hits" value={metrics?.cache_hits ?? 0} />
          <Metric label="Cache misses" value={metrics?.cache_misses ?? 0} />
          <Metric label="Hit rate" value={`${metrics?.hit_rate_pct ?? 0}%`} />
          <Metric label="Errors" value={metrics?.errors ?? 0} />
          <Metric label="Avg" value={`${metrics?.avg_duration_ms ?? 0} ms`} />
          <Metric label="p95" value={`${metrics?.p95_duration_ms ?? 0} ms`} />
          <Metric label="DB lookup avg" value={`${metrics?.db_lookup_avg_ms ?? 0} ms`} />
          <Metric label="Window samples" value={metrics?.samples_in_window ?? 0} />
        </div>
        {metrics?.last_cache_cleared_at && (
          <p className="text-xs text-slate-400 mt-2">
            Last cache cleared at {new Date(metrics.last_cache_cleared_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-md bg-slate-50 p-2.5">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 2) Bulk Apply + Seed Packs tab
// ─────────────────────────────────────────────────────────────────────────
export function BulkAndPacksPanel() {
  const currentYear = new Date().getFullYear();
  const [country, setCountry] = useState('CA');
  const [year, setYear] = useState(currentYear);
  const [provinceAware, setProvinceAware] = useState(true);

  const [showConfirm, setShowConfirm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const [packs, setPacks] = useState([]);
  const [packYear, setPackYear] = useState(currentYear);
  const [applyingPack, setApplyingPack] = useState(null);

  useEffect(() => {
    holidayAPI.seedPacks().then((r) => setPacks(r.data.packs || [])).catch(() => {});
  }, []);

  const openConfirm = async () => {
    setApplying(true);
    try {
      const r = await holidayAPI.applyCountryYear({ country_code: country, year, province_aware: provinceAware });
      setPreview(r.data);
      setShowConfirm(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to compute preview');
    } finally {
      setApplying(false);
    }
  };

  const confirmApply = async () => {
    setApplying(true);
    try {
      const r = await holidayAPI.applyCountryYear({ country_code: country, year, province_aware: provinceAware });
      setResult(r.data);
      toast.success(`Applied — ${r.data.holidays_applied} holiday rows across ${r.data.locations_affected} locations`);
      setShowConfirm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleApplyPack = async (pack) => {
    setApplyingPack(pack.key);
    try {
      const r = await holidayAPI.applySeedPack(pack.key, { year: packYear });
      toast.success(`${pack.label}: ${r.data.applied} applied, ${r.data.skipped} skipped (idempotent)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pack apply failed');
    } finally {
      setApplyingPack(null);
    }
  };

  const yearOpts = useMemo(() => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2], [currentYear]);

  return (
    <div className="space-y-6">
      {/* Bulk apply */}
      <div className="rounded-lg border p-4 bg-white" data-testid="bulk-apply-panel">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="h-4 w-4 text-slate-500" />
          <h3 className="font-medium">Apply Country Holidays to All Locations</h3>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Province-aware: e.g. Quebec holidays apply only to Quebec locations. Idempotent — re-running is safe and never creates duplicates. Tenant overrides are always preserved.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Country</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="bulk-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['CA', 'US', 'GB', 'AU', 'NZ', 'FR', 'PH'].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="bulk-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOpts.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-end gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={provinceAware}
              onChange={(e) => setProvinceAware(e.target.checked)}
              data-testid="bulk-province-aware"
            />
            Province-aware
          </label>
          <div className="flex items-end">
            <Button
              onClick={openConfirm}
              disabled={applying}
              data-testid="bulk-apply-btn"
            >
              {applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Apply {country} {year}
            </Button>
          </div>
        </div>
        {result && (
          <div className="mt-3 rounded-lg border bg-slate-50 p-3 text-sm" data-testid="bulk-apply-result">
            <div className="font-medium">Last apply result</div>
            <div className="text-slate-600">
              {result.locations_affected} locations · {result.holidays_applied} applied · {result.skipped_duplicates} skipped
            </div>
            {(result.warnings || []).length > 0 && (
              <ul className="mt-2 text-xs text-amber-700 list-disc pl-4">
                {result.warnings.map((w, i) => <li key={i}><b>{w.location_name}:</b> {w.message}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Seed packs */}
      <div className="rounded-lg border p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-slate-500" />
            <h3 className="font-medium">Province / State Holiday Packs</h3>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Year</Label>
            <Select value={String(packYear)} onValueChange={(v) => setPackYear(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOpts.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {packs.map((p) => (
            <div key={p.key} className="rounded-lg border p-3 flex items-center justify-between" data-testid={`seed-pack-${p.key}`}>
              <div>
                <div className="font-medium text-sm">{p.label}</div>
                <div className="text-xs text-slate-500">{p.country_code} · {p.province_state_code} · {p.holidays_count} holidays</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleApplyPack(p)}
                disabled={applyingPack === p.key || p.holidays_count === 0}
                data-testid={`apply-pack-${p.key}`}
              >
                {applyingPack === p.key ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
                Enable
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Confirm modal */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply {country} {year} holidays?</DialogTitle>
            <DialogDescription>
              {preview ? `${preview.locations_affected} location(s) match this country.` : '...'}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="rounded border p-3 bg-slate-50">
                <div><b>{preview.holidays_applied}</b> holidays will be active across the matched locations.</div>
                <div><b>{preview.skipped_duplicates}</b> existing override(s) preserved.</div>
              </div>
              {Object.keys(preview.province_breakdown || {}).length > 0 && (
                <div>
                  <Label className="text-xs">Province breakdown</Label>
                  <ul className="text-xs text-slate-600 list-disc pl-4">
                    {Object.entries(preview.province_breakdown).map(([k, v]) => (
                      <li key={k}>{k}: {v} holidays</li>
                    ))}
                  </ul>
                </div>
              )}
              {(preview.warnings || []).length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  <b>Warnings:</b>
                  <ul className="list-disc pl-4 mt-1">
                    {preview.warnings.map((w, i) => <li key={i}>{w.location_name}: {w.message}</li>)}
                  </ul>
                </div>
              )}
              <p className="text-xs text-slate-400">
                {preview.note}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button onClick={confirmApply} disabled={applying} data-testid="bulk-apply-confirm">
              {applying && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Confirm Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 3) Upcoming Impact (READ-ONLY forecast)
// ─────────────────────────────────────────────────────────────────────────
export function UpcomingImpactPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [countryCode, setCountryCode] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (countryCode) params.country_code = countryCode;
      const r = await holidayAPI.upcomingImpact(params);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load impact');
    } finally {
      setLoading(false);
    }
  }, [days, countryCode]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-slate-500" />
            <h3 className="font-medium">Upcoming Holiday Impact</h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-32" data-testid="impact-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="14">Next 14 days</SelectItem>
                <SelectItem value="30">Next 30 days</SelectItem>
                <SelectItem value="60">Next 60 days</SelectItem>
                <SelectItem value="90">Next 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={countryCode || 'all'} onValueChange={(v) => setCountryCode(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-28" data-testid="impact-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {['CA', 'US', 'GB', 'AU', 'NZ', 'FR', 'PH'].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="impact-refresh">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Forecast only — no payroll calculations are modified. Multipliers come from existing payroll constants.
        </p>
        {data && (
          <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
            <Metric label="Upcoming holidays" value={data.totals?.holidays ?? 0} />
            <Metric label="Shifts on holidays" value={data.totals?.shifts_scheduled ?? 0} />
            <Metric label="Est. premium impact" value={`+$${(data.totals?.estimated_premium_impact ?? 0).toLocaleString()}`} />
          </div>
        )}
        <div className="rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">In</th>
                <th className="text-left px-3 py-2 font-medium">Holiday</th>
                <th className="text-left px-3 py-2 font-medium">×</th>
                <th className="text-right px-3 py-2 font-medium">Shifts</th>
                <th className="text-right px-3 py-2 font-medium">Locations</th>
                <th className="text-right px-3 py-2 font-medium">Employees</th>
                <th className="text-right px-3 py-2 font-medium">Est. premium</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400" data-testid="impact-empty">
                  No upcoming holidays in this window.
                </td></tr>
              ) : (
                (data?.items || []).map((it) => (
                  <tr key={`${it.holiday_date}-${it.country_code}`} className="border-t" data-testid={`impact-row-${it.holiday_date}`}>
                    <td className="px-3 py-2 font-mono text-xs">{it.holiday_date}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{it.in_days != null ? `${it.in_days}d` : '—'}</td>
                    <td className="px-3 py-2">
                      <div>{it.holiday_name}</div>
                      <div className="text-xs text-slate-500">{it.country_code}{it.province_state_code ? ` · ${it.province_state_code}` : ''}</div>
                    </td>
                    <td className="px-3 py-2">{it.multiplier?.toFixed?.(2) || it.multiplier}×</td>
                    <td className="px-3 py-2 text-right">{it.shifts_scheduled}</td>
                    <td className="px-3 py-2 text-right">{it.affected_locations}</td>
                    <td className="px-3 py-2 text-right">{it.affected_employees}</td>
                    <td className="px-3 py-2 text-right text-emerald-700 tabular-nums">+${(it.estimated_premium_impact || 0).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 4) PH Audit Trail viewer
// ─────────────────────────────────────────────────────────────────────────
export function PHAuditTrailPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await holidayAPI.phAuditLog({ limit: 100 });
      setItems(r.data.items || []);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-lg border p-4 bg-white" data-testid="ph-audit-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-rose-500" />
          <h3 className="font-medium">PH Override Audit Trail</h3>
          <Badge variant="outline" className="ml-2 text-xs">Append-only · PH only</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="ph-audit-empty">No PH override actions logged yet.</p>
      ) : (
        <ol className="space-y-2">
          {items.map((it) => (
            <li key={it.id} className="rounded border p-3 bg-slate-50/50 text-sm" data-testid={`ph-audit-${it.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold uppercase text-xs mr-2">{it.action}</span>
                  <span className="text-slate-600">{it.after?.holiday_name || it.before?.holiday_name || '—'}</span>
                  {it.after?.holiday_date && (
                    <span className="ml-2 text-xs text-slate-500 font-mono">{it.after.holiday_date}</span>
                  )}
                </div>
                <span className="text-xs text-slate-400">{new Date(it.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                By {it.actor_email || it.actor_user_id || 'system'}
              </div>
              {it.reason && (
                <div className="text-xs mt-1.5 text-slate-700"><b>Reason:</b> {it.reason}</div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 5) Feature Flags tab (owner + super_admin)
// ─────────────────────────────────────────────────────────────────────────
export function FeatureFlagsPanel() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const [confirm, setConfirm] = useState(null); // {flag, enabled, reason}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await holidayAPI.featureFlags();
      setFlags(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requestToggle = (f) => {
    setConfirm({ flag: f.flag, enabled: !f.enabled, reason: '' });
  };

  const submitToggle = async () => {
    if (!confirm) return;
    try {
      await holidayAPI.setFeatureFlag(confirm.flag, {
        enabled: confirm.enabled,
        reason: confirm.reason || null,
      });
      toast.success(`${confirm.flag} ${confirm.enabled ? 'enabled' : 'disabled'}`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    }
  };

  const loadAudit = async () => {
    try {
      const r = await holidayAPI.featureFlagAudit();
      setAudit(r.data.items || []);
      setAuditOpen(true);
    } catch {/* silent */}
  };

  return (
    <div className="rounded-lg border p-4 bg-white" data-testid="feature-flags-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-indigo-500" />
          <h3 className="font-medium">Holiday Feature Flags</h3>
          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">Experimental</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={loadAudit} data-testid="flags-audit-btn">View audit log</Button>
      </div>
      <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 mb-3">
        <b>Warning:</b> These flags are dormant infrastructure for the future holiday-aware payroll rollout. Toggling them <i>does not currently</i> change any payroll behavior. All toggles are audit-logged.
      </div>
      <div className="space-y-2">
        {flags.map((f) => (
          <div key={f.flag} className="flex items-start justify-between rounded border p-3" data-testid={`flag-row-${f.flag}`}>
            <div>
              <div className="font-medium text-sm">{f.label || f.flag}</div>
              <div className="text-xs text-slate-500">{f.description || ''}</div>
              <div className="text-xs text-slate-400 mt-1 font-mono">{f.flag}</div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                className={f.enabled ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}
              >
                {f.enabled ? 'On' : 'Off'}
              </Badge>
              <Button
                size="sm"
                variant={f.enabled ? 'outline' : 'default'}
                onClick={() => requestToggle(f)}
                data-testid={`toggle-flag-${f.flag}`}
              >
                {f.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Confirm toggle modal */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {confirm?.enabled ? 'Enable' : 'Disable'} {confirm?.flag}?
            </DialogTitle>
            <DialogDescription>
              This action is audit-logged. These flags are not yet wired into payroll — toggling has no immediate operational impact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea
              value={confirm?.reason || ''}
              onChange={(e) => setConfirm((c) => ({ ...c, reason: e.target.value }))}
              placeholder="Why are you toggling this flag?"
              data-testid="flag-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button onClick={submitToggle} data-testid="flag-confirm-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Feature flag audit log</DialogTitle>
          </DialogHeader>
          {audit.length === 0 ? (
            <p className="text-sm text-slate-400">No toggle history yet.</p>
          ) : (
            <ol className="space-y-2 max-h-96 overflow-auto">
              {audit.map((a) => (
                <li key={a.id} className="rounded border p-2 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs">{a.flag}</div>
                    <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs mt-1">
                    {a.before?.enabled ? 'on' : 'off'} → <b>{a.after?.enabled ? 'on' : 'off'}</b> · by {a.actor_email || a.actor_user_id}
                  </div>
                  {a.reason && <div className="text-xs text-slate-600 mt-1">{a.reason}</div>}
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
